import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, IndianRupee, FileSpreadsheet, Receipt, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';
import { EmployeeLink } from '../components/links/EntityLinks';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

/**
 * Professional Tax Report
 *
 * Per-employee breakdown of PT actually deducted against **paid** payrolls
 * over an Indian Financial Year (Apr–Mar) or a custom YYYY-MM range. Powers
 * the periodic PT return that has to be filed with the State Treasury.
 */
export const ProfessionalTaxReportPage = () => {
  const today = useMemo(() => new Date(), []);
  // Default to current FY (Apr–Mar)
  const defaultFy = useMemo(() => {
    const y = today.getFullYear();
    const start = today.getMonth() >= 3 ? y : y - 1;
    return `${start}-${String(start + 1).slice(-2)}`;
  }, [today]);

  const [mode, setMode] = useState('fy');  // 'fy' | 'custom'
  const [fy, setFy] = useState(defaultFy);
  const [fromMonth, setFromMonth] = useState(`${today.getFullYear()}-04`);
  const [toMonth, setToMonth] = useState(`${today.getFullYear()}-06`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const fyOptions = useMemo(() => {
    const opts = [];
    for (let y = today.getFullYear() + 1; y >= 2020; y--) {
      opts.push(`${y}-${String(y + 1).slice(-2)}`);
    }
    return opts;
  }, [today]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mode === 'fy') params.set('fy', fy);
      else { params.set('from', fromMonth); params.set('to', toMonth); }
      const res = await api.get(`/payroll/professional-tax-report?${params.toString()}`);
      if (res.data?.success) setData(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setData(null);
    } finally { setLoading(false); }
  }, [mode, fy, fromMonth, toMonth]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleExport = () => {
    if (!data?.rows?.length) { toast.info('No rows to export'); return; }
    // Flatten: one row per employee × month so the spreadsheet is filing-ready.
    const flat = [];
    data.rows.forEach(r => {
      if (!r.monthly_breakdown?.length) {
        flat.push({ employee_name: r.employee_name, employee_code: r.employee_code,
                    month: '', amount: 0, payment_date: '' });
        return;
      }
      r.monthly_breakdown.forEach(m => {
        flat.push({
          employee_name: r.employee_name,
          employee_code: r.employee_code,
          month: m.month,
          amount: m.amount,
          payment_date: m.payment_date,
        });
      });
    });
    exportToExcel(flat, [
      { key: 'employee_name', header: 'Employee' },
      { key: 'employee_code', header: 'Code' },
      { key: 'month', header: 'Month' },
      { key: 'amount', header: 'PT Deducted' },
      { key: 'payment_date', header: 'Payment Date' },
    ], `PT_Report_${data.period_label.replace(/\s+/g, '_')}`);
    toast.success(`Exported ${flat.length} rows`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pt-report-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="h-6 w-6 text-amber-500" />Professional Tax Report</h1>
          <p className="text-muted-foreground">Per-employee PT deductions against <strong>paid</strong> payrolls — ready for State Treasury filing</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data?.rows?.length} data-testid="pt-export-btn">
          <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
        </Button>
      </div>

      <Card>
        <CardContent className="py-4 grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Period</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="pt-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fy">Financial Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'fy' ? (
            <div className="space-y-1.5">
              <Label className="text-xs">Financial Year</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger className="max-w-xs" data-testid="pt-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent>{fyOptions.map(f => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} data-testid="pt-from-month" /></div>
              <div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} data-testid="pt-to-month" /></div>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card className="stat-card"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Period</p>
              <p className="text-lg font-bold">{data.period_label}</p>
            </CardContent></Card>
            <Card className="stat-card"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Employees Liable</p>
              <p className="text-2xl font-bold" data-testid="pt-employee-count">{data.employee_count}</p>
            </CardContent></Card>
            <Card className="stat-card border-amber-500/20"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Grand Total PT</p>
              <p className="text-2xl font-bold text-amber-500" data-testid="pt-grand-total">{formatCurrency(data.grand_total)}</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-0">
              {data.rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <IndianRupee className="h-12 w-12 mb-3 opacity-50" />
                  <p>No paid payrolls fall in this period.</p>
                  <p className="text-xs mt-1">PT only counts toward this report once a payslip is marked as paid.</p>
                </div>
              ) : (
                <Table data-testid="pt-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Months Paid</TableHead>
                      <TableHead className="text-right">Total PT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map(r => (
                      <React.Fragment key={r.employee_id}>
                        <TableRow data-testid={`pt-row-${r.employee_id}`}>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleExpand(r.employee_id)} data-testid={`pt-expand-${r.employee_id}`}>
                              {expanded.has(r.employee_id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                          <TableCell className="font-medium"><EmployeeLink id={r.employee_id} name={r.employee_name} /></TableCell>
                          <TableCell className="font-mono text-sm text-muted-foreground">{r.employee_code || '—'}</TableCell>
                          <TableCell>{r.months_count}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(r.total_pt)}</TableCell>
                        </TableRow>
                        {expanded.has(r.employee_id) && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={5} className="py-3">
                              <table className="w-full text-xs">
                                <thead className="text-muted-foreground">
                                  <tr><th className="text-left py-1 pl-12">Month</th><th className="text-left">Payment Date</th><th className="text-right pr-4">Amount</th></tr>
                                </thead>
                                <tbody>
                                  {r.monthly_breakdown.map((m, i) => (
                                    <tr key={i}><td className="py-1 pl-12">{m.month}</td><td>{m.payment_date || '—'}</td><td className="text-right pr-4 font-mono">{formatCurrency(m.amount)}</td></tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
